import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Removed Redux auth selectors - now using useAuth() hook
import '../../styles/components.css';
import '../../styles/patientTables.css';
import PatientListBase from './PatientListBase';
import { Snackbar, Alert } from '@mui/material';
// Use service layer instead of direct Firestore access
import { getPatientsByFacility } from '../../services/patientService';
import { getAllFacilities } from '../../services/facilityService';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { useUserData } from '../../hooks/useUserData';


const PatientList = () => {
  const navigate = useNavigate();
  const { role, defaultFacility, user } = useUserData();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [facilities, setFacilities] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [lastFacilityId, setLastFacilityId] = useState(null);

  // Only use recent searches when user is authenticated and we have a facility
  const { 
    recentSearches, 
    navigateToPatient, 
    clearRecentSearches 
  } = useRecentSearches(user?.uid && defaultFacility?.id ? defaultFacility.id : null);


  const fetchPatients = useCallback(async () => {
    try {
      // Get current facility ID - user only has one default facility
      const facilityId = defaultFacility?.id;
      
      console.log('fetchPatients called with facilityId:', facilityId, 'lastFacilityId:', lastFacilityId, 'loading:', loading);
      
      // Skip if no facility or if facility hasn't changed
      if (!facilityId) {
        setPatients([]);
        setError('No facility access available. Please contact your administrator.');
        setLastFacilityId(null);
        return;
      }

      // Skip if we're already loading for this facility
      if (lastFacilityId === facilityId && loading) {
        console.log('Skipping fetch - already loading for this facility');
        return;
      }

      setLoading(true);
      setError(null);
      setLastFacilityId(facilityId);

      const patientList = await getPatientsByFacility(facilityId);
      setPatients(patientList);
      
      // Show success message - use facility name from defaultFacility
      const facilityName = defaultFacility?.name || facilityId;
      setSnackbar({
        open: true,
        message: `Loaded patients from facility: ${facilityName}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError('Error fetching patients');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, defaultFacility?.name, lastFacilityId, loading]); // Include both id and name

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const facilitiesData = await getAllFacilities();
        const facilitiesMap = {};
        facilitiesData.forEach(facility => {
          facilitiesMap[facility.id] = facility.name;
        });
        setFacilities(facilitiesMap);
      } catch (error) {
        console.error('Error fetching facilities:', error);
        setError('Error fetching facilities');
      }
    };

    fetchFacilities();
  }, []);

  // Only fetch patients when facility ID changes
  useEffect(() => {
    const facilityId = defaultFacility?.id;
    if (facilityId && facilityId !== lastFacilityId) {
      fetchPatients();
    }
  }, [defaultFacility?.id, lastFacilityId, fetchPatients]);

  // Initial fetch when component mounts and we have a facility
  useEffect(() => {
    const facilityId = defaultFacility?.id;
    console.log('Initial fetch effect triggered:', { facilityId, lastFacilityId, defaultFacilityId: defaultFacility?.id });
    if (facilityId && !lastFacilityId) {
      console.log('Initial fetch needed, calling fetchPatients');
      fetchPatients();
    }
  }, [defaultFacility?.id, lastFacilityId, fetchPatients]);

  // Handle patient click - navigate and add to recent searches
  const handlePatientClick = useCallback(async (patient) => {
    await navigateToPatient(patient, navigate);
  }, [navigateToPatient, navigate]);

  // Handle recent search click
  const handleRecentSearchClick = useCallback(async (search) => {
    // Find the patient data to add to recent searches
    const patient = patients.find(p => p.id === search.id);
    if (patient) {
      await navigateToPatient(patient, navigate);
    } else {
      // If patient not found in current list, just navigate
      navigate(`/patients/${search.id}`);
    }
  }, [navigateToPatient, navigate, patients]);

  // Handle clearing recent searches
  const handleClearRecentSearches = useCallback(async () => {
    try {
      await clearRecentSearches();
      setSnackbar({
        open: true,
        message: 'Recent searches cleared successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error clearing recent searches:', error);
      setSnackbar({
        open: true,
        message: 'Error clearing recent searches',
        severity: 'error'
      });
    }
  }, [clearRecentSearches]);



  return (
    <>
      <PatientListBase
        patients={patients}
        loading={loading}
        error={error}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onPatientClick={handlePatientClick}
        onCreatePatient={() => navigate('/patients/new')}
        showRecentSearches={true}
        recentSearches={recentSearches}
        onRecentSearchClick={handleRecentSearchClick}
        onClearRecentSearches={handleClearRecentSearches}
        facilities={facilities}
        role={role}
      />

      {/* Success/Info Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PatientList;
